import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { getTaskListApi, getCommentListByTaskIdApi, getAnalysisProgressApi } from '../Api/api.js';
import { Layout, Table, Tag } from '@arco-design/web-react';
import { useParams } from 'react-router-dom';
import FilterBar from '../Components/Analysis/FilterBar';
import SummaryCards from '../Components/Analysis/SummaryCards';
import TableSummary from '../Components/Analysis/TableSummary';
import SidebarMenu from "../Components/Analysis/SidebarMenu";
import HomeHeader from '../Components/Home/HomeHeader';
import TableFilter from '../Components/Common/TableFilter';
export default function Analysis() {
  // ---------- 1. 任务与评论相关状态 ----------
  const [tasks, setTasks] = useState([]);
  const [taskLoading, setTaskLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedTaskId, setSelectedTaskId] = useState(() => {
    // 从localStorage恢复缓存的任务ID
    return localStorage.getItem('selectedTaskId') || null;
  });
  // 评论相关状态
  const [comments, setComments] = useState([]);
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentTotal, setCommentTotal] = useState(0);
  const [commentCurrent, setCommentCurrent] = useState(1);
  const [commentPageSize, setCommentPageSize] = useState(10);
 
// ---------- 2. 进度与输入相关状态 ----------
const [progressData, setProgressData] = useState({
  progressCurrent: 0,
  total: 0,
  intentYesCount: 0,
  intentPercent: 0
});
const [inputValue, setInputValue] = useState('');

// ---------- 3. 筛选相关状态 ----------
const [filters, setFilters] = useState({
  userNickname: '',
  userProvince: '',
  commentContent: '',
  intentCustomer: ''
});

// 筛选处理函数
const handleFilter = (field, value) => {
  setFilters(prev => ({
    ...prev,
    [field]: value
  }));
};

// 筛选后的评论数据
const filteredComments = useMemo(() => {
  return comments.filter(comment => {
    const matchNickname = !filters.userNickname || 
      (comment['用户昵称'] && comment['用户昵称'].toLowerCase().includes(filters.userNickname.toLowerCase()));
    const matchProvince = !filters.userProvince || 
      (comment['IP地址'] && comment['IP地址'].toLowerCase().includes(filters.userProvince.toLowerCase()));
    const matchContent = !filters.commentContent || 
      (comment['评论内容'] && comment['评论内容'].toLowerCase().includes(filters.commentContent.toLowerCase()));
    const matchIntent = !filters.intentCustomer || 
      (comment['意向客户'] && comment['意向客户'].toLowerCase().includes(filters.intentCustomer.toLowerCase()));
    
    return matchNickname && matchProvince && matchContent && matchIntent;
  });
}, [comments, filters]);

// ---------- 4. 数据获取 ----------
// 3.1 获取任务列表

useEffect(() => {
  async function fetchTasks() {
    setTaskLoading(true);
    try {
      const res = await getTaskListApi(0, 1000);
      if (res && res.status === 200 && res.data && Array.isArray(res.data.task_list)) {
        const taskList = res.data.task_list;
        
        // 只获取基本任务信息，不获取进度数据
        // 进度数据将在选中特定任务时按需获取
        const tasksWithBasicInfo = taskList.map(task => ({
          ...task,
          progress: 0, // 默认进度为0
          progressData: { num: 0, sum: 0 } // 默认进度数据
        }));
        
        setTasks(tasksWithBasicInfo);
      } else {
        setTasks([]);
      }
    } catch (error) {
      console.error('获取任务列表失败:', error);
      setTasks([]);
    } finally {
      setTaskLoading(false);
    }
  }
  fetchTasks();
}, []);

// 3.2 获取选中任务的进度数据
const fetchTaskProgress = useCallback(async (taskId) => {
  if (!taskId) return;
  
  try {
    const progressRes = await getAnalysisProgressApi(taskId);
    if (progressRes && progressRes.status === 200 && progressRes.data) {
      const { num = 0, sum = 0 } = progressRes.data;
      const progress = sum > 0 ? Math.round((num / sum) * 100) : 0;
      
      // 更新对应任务的进度数据
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.task_id === taskId 
            ? { ...task, progress, progressData: progressRes.data }
            : task
        )
      );
    }
  } catch (error) {
    console.error('获取任务进度失败:', error);
  }
}, []); // setState函数是稳定的，不需要依赖

// 3.3 当选中任务变化时，获取该任务的进度数据
useEffect(() => {
  if (selectedTaskId) {
    fetchTaskProgress(selectedTaskId);
  }
}, [selectedTaskId]); // 移除fetchTaskProgress依赖，避免无限循环

 // 3.4 选中任务变化时自动请求评论
 const fetchComments = useCallback(async (taskId, page = 1, size = 10) => {
  if (!taskId) return;
  setCommentLoading(true);
  try {

    const res = await getCommentListByTaskIdApi(taskId, page, size);

    if (res && res.status === 200 && res.data) {
      setComments((res.data.comment_list || []).map(item => {
        return {
          ...item,
          user_url: item['用户链接'] || '',      // 正确赋值
          video_url: item['内容链接'] || '',     // 正确赋值
        };
      }));
      setCommentTotal(res.data.total || 0);
    } else {

      setComments([]);
      setCommentTotal(0);
    }
  } catch (error) {
    
    setComments([]);
    setCommentTotal(0);
  } finally {
    setCommentLoading(false);
  }
}, []); // setState函数是稳定的，不需要依赖
 // 3.5 监听任务变化或页面大小变化，重置分页并获取评论
 useEffect(() => {
  if (selectedTaskId) {
    setCommentCurrent(1); // 重置到第一页
    setComments([]); // 立即清空评论数据，避免显示上一个任务的数据
    setCommentTotal(0); // 重置总数
    fetchComments(selectedTaskId, 1, commentPageSize); // 直接用第1页获取数据
  } else {
    // 如果没有选中任务，清空所有数据
    setComments([]);
    setCommentTotal(0);
    setCommentCurrent(1);
  }
}, [selectedTaskId, commentPageSize]); // 移除fetchComments依赖，避免无限循环

 // 3.6 任务列表变化时的选择逻辑
 useEffect(() => {
  if (tasks.length > 0 && !selectedTaskId) {
    // 只有在没有选中任务时才自动选择
    const cachedTaskId = localStorage.getItem('selectedTaskId');
    if (cachedTaskId && tasks.some(task => task.task_id === cachedTaskId)) {
      // 缓存的任务ID有效，使用缓存的任务
      setSelectedTaskId(cachedTaskId);
    } else {
      // 没有有效的缓存，选择第一个任务
      const firstTaskId = tasks[0].task_id;
      setSelectedTaskId(firstTaskId);
    }
  }
}, [tasks, selectedTaskId]); // 简化逻辑，避免复杂条件判断
// ---------- 4. 进度统计 ----------

  // 当页码变化时获取对应页面的数据
   useEffect(() => {
     if (selectedTaskId && commentCurrent > 1) {
       fetchComments(selectedTaskId, commentCurrent, commentPageSize);
     }
   }, [commentCurrent, selectedTaskId, commentPageSize]); // 移除fetchComments依赖，避免无限循环
  useEffect(() => {
    // 评论相关统计 - 基于筛选后的数据
    const total = filteredComments.length;
    const intentYesCount = filteredComments.filter(item => item['意向客户'] === '是').length;
    const intentNoCount = filteredComments.filter(item => item['意向客户'] === '否').length;
    const intentPercent = total > 0 ? Math.round((intentYesCount / total) * 100) : 0;
    const progressPercent = total > 0 ? 100 : 0;
  
    // 任务相关统计
    const completedTasks = tasks.filter(task =>
      task.analysis_state === 'finish' && task.crawler_state === 'finish'
    ).length;
    const totalTasks = tasks.length;
    const intentCount = tasks.reduce((sum, task) => sum + (task.intent_count || 0), 0);
    const intentPercentage = totalTasks > 0 ? Math.round((intentCount / totalTasks) * 100) : 0;
  
    setProgressData({
      // 评论相关 - 显示筛选后的统计
      commentTotal: total,
      intentYesCount,
      intentNoCount,
      intentPercent,
      progressPercent,
      // 任务相关
      completedTasks,
      totalTasks,
      intentCount,
      intentPercentage
    });
  }, [filteredComments, tasks]);
 // ---------- 5. 分页处理 ----------
 const handleCommentPageChange = (page, size) => {
  setCommentCurrent(page);
  setCommentPageSize(size);
};
  // ---------- 6. 表格列配置 ----------
  const columns = [
    { 
      title: (
        <div style={{ minHeight: '32px', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
          序号
        </div>
      ), 
      dataIndex: 'index', 
      key: 'index', 
      render: (_, __, idx) => (commentCurrent - 1) * commentPageSize + idx + 1 
    },
    { 
      title: (
        <div style={{ minHeight: '32px', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
          评论时间
        </div>
      ), 
      dataIndex: '评论时间', 
      key: '评论时间', 
      render: v => v || '-' 
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minHeight: '32px', width: '100%', whiteSpace: 'nowrap' }}>
          <span>用户昵称</span>
          <TableFilter
            onFilter={(value) => handleFilter('userNickname', value)}
            placeholder="请输入用户昵称"
          />
        </div>
      ),
      dataIndex: '用户昵称',
      key: '用户昵称',
      render: (text, record) => (
        record.user_url ? (
          <a
            href={record.user_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#165DFF' }}
          >
            {text}
          </a>
        ) : text
      )
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minHeight: '32px', width: '100%', whiteSpace: 'nowrap' }}>
          <span>用户省份</span>
          <TableFilter
            onFilter={(value) => handleFilter('userProvince', value)}
            placeholder="请输入省份"
          />
        </div>
      ),
      dataIndex: 'IP地址',
      key: 'IP地址'
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minHeight: '32px', width: '100%', whiteSpace: 'nowrap' }}>
          <span>评论内容</span>
          <TableFilter
            onFilter={(value) => handleFilter('commentContent', value)}
            placeholder="请输入评论内容关键词"
          />
        </div>
      ),
      dataIndex: '评论内容',
      key: '评论内容',
      render: (text, record) => (
        record.video_url ? (
          <a
            href={record.video_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#165DFF' }}
          >
            {text}
          </a>
        ) : text
      )
    },
    { 
      title: (
        <div style={{ minHeight: '32px', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
          分析理由
        </div>
      ), 
      dataIndex: '分析理由', 
      key: '分析理由', 
      render: v => <div>{v || '-'}</div> 
    },
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minHeight: '32px', width: '100%', whiteSpace: 'nowrap' }}>
          <span>意向客户</span>
          <TableFilter
            onFilter={(value) => handleFilter('intentCustomer', value)}
            placeholder="请输入是/否"
          />
        </div>
      ),
      dataIndex: '意向客户',
      key: '意向客户',
      render: v =>
        v === '是' ? (
          <Tag color="green">高意向</Tag>
        ) : v === '否' ? (
          <Tag color="gray">无意向</Tag>
        ) : (
          <Tag color="gray">{v || '-'}</Tag>
        )
    }
  ];
  const currentTask = useMemo(() => {
    return tasks.find(item => item.task_id === selectedTaskId);
  }, [tasks, selectedTaskId]);
  const inputPlaceholder = currentTask ? currentTask.keyword : "短视频代剪辑";
  const { taskId } = useParams();
  
  // 任务更新回调函数
  const handleTaskUpdate = useCallback(async () => {
    if (selectedTaskId) {
      try {
        // 重新获取当前任务的详细信息
        const res = await getTaskListApi(0, 1000);
        if (res && res.status === 200 && res.data && Array.isArray(res.data.task_list)) {
          const taskList = res.data.task_list;
          const updatedTask = taskList.find(task => task.task_id === selectedTaskId);
          if (updatedTask) {
            // 更新任务列表中的对应任务
            setTasks(prevTasks => 
              prevTasks.map(task => 
                task.task_id === selectedTaskId ? { ...task, ...updatedTask } : task
              )
            );
          }
        }
      } catch (error) {
        console.error('更新任务状态失败:', error);
      }
    }
  }, [selectedTaskId]);
  
  // 处理URL参数中的taskId
  useEffect(() => {
    if (taskId && taskId !== selectedTaskId) {
      setSelectedTaskId(taskId);
      localStorage.setItem('selectedTaskId', taskId);
    }
  }, [taskId, selectedTaskId]);
  
  // 缓存选中的任务ID（避免频繁更新）
  useEffect(() => {
    if (selectedTaskId) {
      const cachedTaskId = localStorage.getItem('selectedTaskId');
      if (cachedTaskId !== selectedTaskId) {
        localStorage.setItem('selectedTaskId', selectedTaskId);
      }
    }
  }, [selectedTaskId]);
  return (
    <Layout style={{ height: '100vh', background: '#fff' }}>
      <Layout.Header style={{ width: '100%', padding: 0, background: '#fff' }}>
        <HomeHeader />
      </Layout.Header>
      <Layout style={{ height: 'calc(100vh - 64px)', flexDirection: 'row',marginLeft: 10,marginRight: 10 }}>
        <SidebarMenu 
          tasks={tasks} 
          loading={taskLoading}
          selectedTaskId={selectedTaskId}
          onTaskSelect={setSelectedTaskId}
        />
        <Layout.Content style={{ flex: 1, margin: 16, background: '#fff', borderRadius: 8, minWidth: 0 }}>
      
        <FilterBar
          inputValue={inputValue}
          setInputValue={setInputValue}
          placeholder={inputPlaceholder}
          currentTask={currentTask}
          onTaskUpdate={handleTaskUpdate}
        />
          <SummaryCards currentTask={currentTask} />
          <TableSummary
            total={commentTotal}
            pageSize={commentPageSize}
            current={commentCurrent}
            setCurrent={setCommentCurrent}
            setPageSize={setCommentPageSize}
            progressData={progressData}
            taskId={selectedTaskId}
          />
          <div style={{ margin: '20px 1px' }}>
          
            <Table
              columns={columns}
              data={filteredComments}
               rowKey="comment_id"
              loading={commentLoading}
              style={{ marginTop: 16 }}
              pagination={false}   // 关键：关闭自带分页器
            />
          </div>
        </Layout.Content>
      </Layout>
    </Layout>
  );
}